import React from 'react';
import { Paper, Grid, Button, AppBar, Toolbar, Box, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { UseFormSetValue } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import HookFormControls from '@/components/common/HookFormControls';
import PageTitle from '@/components/common/PageTitle';

// Types & Services
import { EmployeeDepartments } from '@/types/Employee.type';
import Status from '@/types/Status.type';
import Phone from '@/types/Phone.type';
import Style from '@/types/Style.type';
import ActSchedule from '@/types/ActSchedule.type';
import SmallPlanRegistryForm from './SmallPlanRegistryForm';

type Props = {
  employeeDepartments: EmployeeDepartments;
  undecidedFlag: boolean;
  goingStraightHomeFlag: boolean;
  isSubmitting: boolean;
  validationRules: {
    style: {};
    startTime: {};
    endTime: {};
    nextDate: {};
    status: {};
    place: {};
    returnTime: {};
    phone: {};
    goingStraightHomeFlag: {};
    undecidedFlag: {};
  };
  phones: Array<Phone>;
  styles: Array<Style>;
  statuses: Array<Status>;
  handleClick1h: () => void;
  handleClick30m: () => void;
  setValue: UseFormSetValue<ActSchedule>;
  setUndecidedFlag: (undecidedFlag: boolean) => void;
  setGoingStraightHomeFlag: (goingStraightHomeFlag: boolean) => void;
  autoFocus: boolean;
};

const SmallPlanRegistry: React.FC<Props> = ({
  employeeDepartments,
  undecidedFlag,
  goingStraightHomeFlag,
  isSubmitting,
  validationRules,
  phones,
  styles,
  statuses,
  handleClick1h,
  handleClick30m,
  setValue,
  setUndecidedFlag,
  setGoingStraightHomeFlag,
  autoFocus,
}) => {
  const navigate = useNavigate();

  return (
    <>
      <PageTitle>予定登録画面</PageTitle>
      <Paper variant="outlined" sx={{ width: '100%' }}>
        <Grid container fontSize="Small">
          <SmallPlanRegistryForm label="氏名">{employeeDepartments.name}</SmallPlanRegistryForm>
          <SmallPlanRegistryForm label="勤務形態">
            <Grid textAlign="center" pl={3} pr={2}>
              <HookFormControls
                label=""
                type="autocomplete"
                name="style"
                getOptionLabel="name"
                autocompleteProps={{ size: 'small' }}
                rule={validationRules.style}
                options={styles}
              />
            </Grid>
          </SmallPlanRegistryForm>
          <SmallPlanRegistryForm label="勤務時間（カスタム）">
            <Grid pl={3} pr={2}>
              <Grid display="flex" gap={1}>
                <HookFormControls
                  name="startTime"
                  type="time"
                  label="勤務開始時間"
                  size="small"
                  rule={validationRules.startTime}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ maxWidth: 150 }}
                />
                <HookFormControls
                  name="endTime"
                  type="time"
                  label="勤務終了時間"
                  size="small"
                  rule={validationRules.endTime}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 300 }}
                  sx={{ maxWidth: 150 }}
                />
              </Grid>
            </Grid>
          </SmallPlanRegistryForm>
          <SmallPlanRegistryForm label="次回出社日">
            <Grid pl={3}>
              <Grid display="flex" gap={1}>
                <HookFormControls
                  disabled={undecidedFlag}
                  name="nextDate"
                  type="date"
                  label=""
                  size="small"
                  rule={validationRules.nextDate}
                  InputLabelProps={{ shrink: true }}
                  formHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ maxHeight: 40 }}
                  disabled={undecidedFlag}
                  onClick={() => setValue('nextDate', '')}
                >
                  Clear
                </Button>
                <HookFormControls
                  name="undecidedFlag"
                  type="checkbox"
                  size="small"
                  label={<Typography sx={{ fontSize: 'small' }}>未定</Typography>}
                  checkboxprops={{ size: 'small' }}
                  formControlLabelProps={{ sx: { maxHeight: 40 } }}
                  rule={validationRules.undecidedFlag}
                  onClick={() => {
                    setValue('nextDate', '');
                    setUndecidedFlag(!undecidedFlag);
                  }}
                />
              </Grid>
            </Grid>
          </SmallPlanRegistryForm>
          <SmallPlanRegistryForm label="ステータス">
            <Grid pl={3} pr={2}>
              <HookFormControls
                type="autocomplete"
                label=""
                getOptionLabel="name"
                name="status"
                autocompleteProps={{ size: 'small' }}
                rule={validationRules.status}
                options={statuses}
              />
            </Grid>
          </SmallPlanRegistryForm>
          <SmallPlanRegistryForm label="戻り時間">
            <Grid pl={3} pb={1} display="flex" gap={1}>
              <HookFormControls
                name="returnTime"
                type="time"
                label=""
                size="small"
                disabled={goingStraightHomeFlag}
                rule={validationRules.returnTime}
                InputLabelProps={{ shrink: true }}
                formHelperTextProps={{ sx: { whiteSpace: 'nowrap' } }}
                sx={{ width: 150 }}
                autoFocus={autoFocus}
              />
              <HookFormControls
                name="goingStraightHomeFlag"
                type="checkbox"
                size="small"
                label={<Typography sx={{ fontSize: 'small' }}>直帰</Typography>}
                checkboxprops={{ size: 'small' }}
                onClick={() => {
                  setValue('returnTime', '');
                  setGoingStraightHomeFlag(!goingStraightHomeFlag);
                }}
                formControlLabelProps={{ sx: { maxHeight: 40 } }}
                rule={validationRules.goingStraightHomeFlag}
              />
            </Grid>
            <Grid pl={3} display="flex" gap={1}>
              <Button
                variant="outlined"
                size="small"
                disabled={goingStraightHomeFlag}
                sx={{ maxHeight: 40 }}
                onClick={() => setValue('returnTime', '')}
              >
                Clear
              </Button>
              <Button
                disabled={goingStraightHomeFlag}
                sx={{ maxHeight: 40 }}
                variant="contained"
                size="small"
                onClick={handleClick30m}
              >
                +30分
              </Button>
              <Button
                disabled={goingStraightHomeFlag}
                sx={{ maxHeight: 40 }}
                variant="contained"
                size="small"
                onClick={handleClick1h}
              >
                +1時間
              </Button>
            </Grid>
          </SmallPlanRegistryForm>
          <SmallPlanRegistryForm label="行先">
            <Grid textAlign="left" pl={2} pr={2}>
              <HookFormControls
                name="place"
                size="small"
                type="text"
                label="行先"
                rule={validationRules.place}
                multiline
                rows={3}
              />
            </Grid>
          </SmallPlanRegistryForm>
          <SmallPlanRegistryForm label="連絡先">
            <Grid textAlign="left" pl={2} pr={2}>
              <HookFormControls
                type="autocomplete"
                autocompleteProps={{ size: 'small' }}
                name="phone"
                getOptionLabel="number"
                label=""
                rule={validationRules.phone}
                options={phones}
              />
            </Grid>
          </SmallPlanRegistryForm>
        </Grid>
      </Paper>
      <AppBar position="fixed" color="inherit" sx={{ top: 'auto', bottom: 0 }}>
        <Toolbar>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              navigate(-1);
            }}
            sx={{ m: 1 }}
          >
            <b>閉じる</b>
          </Button>
          <LoadingButton variant="contained" size="small" type="submit" color="primary" loading={isSubmitting}>
            <b>登録</b>
          </LoadingButton>
        </Toolbar>
      </AppBar>
    </>
  );
};
export default SmallPlanRegistry;
